import { contentSheetResource, contentTexture, contentTextureGeneration } from "./content_textures"
import { Container, Sprite, Texture, Loader, Graphics, IPointData } from "pixi.js"
import { Bounds, NumberedTileMeta, TileSize } from "game-common/models"
import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"
import { roughToFineCoordinates, setRoughToFineCoordinates } from "game-common/util"
import { CoordinatePool } from "game-common/coordinate_pool"
import { TileTextureExtractor } from "./sprites/tile_texture_extractor"
import { occluderFadePolicy } from "./entity_tile_occluder_fade"
import { GrassSway } from "./effects/grass_sway"
import { bonecallerStoneFrames, bonecallerStoneEnergy } from "./effects/bonecaller_stone"
import { isOreMaterial, oreMineralTexture } from "./effects/ore_mineral_art"
import { getRendererApp } from "./renderer_app"
import { isTreeEntityTile } from "game-common/map/map"
import { BonecallerRitual } from "game-common/bonecaller_combat"
// Where a composite sprite goes and how deep it sorts: shared with Sprite Lab, so it lives on its own.
import { compositeSpritePlacement } from "./composite_tile_placement"
import { rootGroundPoint, treeRootEdges, treeRootFringeTexture } from "./effects/tree_root_fringe"
import { grassAt } from "./effects/grass_feet"


export interface CompositeTileRendererOptions {
    surface: Container
    skySpriteSurfaceAbove: Container
    tilemapMetaProvider: TileMapMetaProvider
    compositeTileSpriteTextures: Map<string, Texture>
    surfaceObjectsByKey: Map<string, any>
    attachTexture: (texture: Texture, surface?: Container) => Sprite
    pertubation: (tileId: string, x: number, y: number, i: number, key: string, multiplier?: number) => number
    pctChance: (chance: number, key: string) => boolean
    logic: any
    tileTextureExtractor: TileTextureExtractor
    getGrassSway?: () => GrassSway | undefined
    depthSortedRoofs?: boolean
}

export class CompositeTileRenderer {
    private bonecallerStones = new Map<Sprite, { shrineId: string; index: number; ritual?: BonecallerRitual; receivedAt: number }>()
    clearBonecallerStones = () => this.bonecallerStones.clear()
    updateBonecallerStones = (now = Date.now()) => {
        if (!this.bonecallerStones.size) return
        const frames = bonecallerStoneFrames(getRendererApp().renderer)
        this.bonecallerStones.forEach((state, sprite) => {
            if (!sprite.parent || sprite.destroyed || !frames.includes(sprite.texture)) {
                this.bonecallerStones.delete(sprite); return
            }
            const ritual = this.logic.entityManager?.get(state.shrineId)?.bonecallerRitual
            if (ritual !== state.ritual) { state.ritual = ritual; state.receivedAt = now }
            const energy = bonecallerStoneEnergy(ritual, state.index, now, now - state.receivedAt)
            sprite.texture = frames[Math.round(energy * (frames.length - 1))]
        })
    }
    private surface: Container
    private skySpriteSurfaceAbove: Container
    private tilemapMetaProvider: TileMapMetaProvider
    private compositeTileSpriteTextures: Map<string, Texture>
    private surfaceObjectsByKey: Map<string, any>
    private attachTexture: (texture: Texture, surface?: Container) => Sprite
    private pertubation: (tileId: string, x: number, y: number, i: number, key: string, multiplier?: number) => number
    private pctChance: (chance: number, key: string) => boolean
    private logic: any
    private tileTextureExtractor: TileTextureExtractor
    // Read lazily rather than held: the renderer that owns the wind is built after this one.
    private getGrassSway: () => GrassSway | undefined
    private depthSortedRoofs: boolean

    constructor(options: CompositeTileRendererOptions) {
        this.surface = options.surface
        this.skySpriteSurfaceAbove = options.skySpriteSurfaceAbove
        this.tilemapMetaProvider = options.tilemapMetaProvider
        this.compositeTileSpriteTextures = options.compositeTileSpriteTextures
        this.surfaceObjectsByKey = options.surfaceObjectsByKey
        this.attachTexture = options.attachTexture
        this.pertubation = options.pertubation
        this.pctChance = options.pctChance
        this.logic = options.logic
        this.tileTextureExtractor = options.tileTextureExtractor
        this.getGrassSway = options.getGrassSway || (() => undefined)
        this.depthSortedRoofs = options.depthSortedRoofs ?? false
    }

    setSurface = (surface: Container): void => {
        this.surface = surface
    }

    /**
     * Offer a piece of vegetation to the wind, and remember what it was offered as on the sprite. A
     * floor drawn while it was not the active one had no wind; when it becomes active its blades are
     * offered again from these (see MapRenderingManager.registerGrassBlades).
     */
    private offerToWind = (sprite: Sprite, x: number, y: number, textureId: string, heightAbove = 0): void => {
        ;(sprite as any).grassBlade = [x, y, textureId, heightAbove]
        this.getGrassSway()?.register(sprite, x, y, textureId, heightAbove)
    }

    public updateTilemapMetaProvider(tilemapMetaProvider: TileMapMetaProvider): void {
        this.tilemapMetaProvider = tilemapMetaProvider
    }

    // Plant art does not own pointer events. Desktop strikes can still resolve a blade to its
    // root by checking its geometry separately from Pixi's interactive hit test.
    public updateCompositeTilePointer = (at: IPointData) => {
        let root: { x: number; y: number } | undefined
        let topZ = -Infinity
        for (const object of this.surfaceObjectsByKey.values()) {
            if (!object.compositeRoot) continue
            for (const sprite of object.spriteSet as Sprite[]) {
                if (sprite.parent && sprite.worldVisible && sprite.zIndex >= topZ && sprite.containsPoint(at)) {
                    root = object.compositeRoot
                    topZ = sprite.zIndex
                }
            }
        }
        this.logic.playerController.updateCompositeTile(root?.x ?? -1, root?.y ?? -1)
    }

    public renderCompositeTile = (
        tilemapMetaProvider: TileMapMetaProvider,
        parentTileId: string,
        compositeTileId: string,
        x: number,
        y: number,
        zIndex: number = 0,
    ) => {
        const key = `${parentTileId}-${x}-${y}`
        const parentTileMeta = tilemapMetaProvider.resolveTileMeta(parentTileId)
        if (parentTileMeta.entityTileId === "bonecaller-stone") {
            if (this.surfaceObjectsByKey.has(key)) return
            const frames = bonecallerStoneFrames(getRendererApp().renderer)
            const sprite = this.attachTexture(frames[0], this.surface)
            sprite.position.set(x * TileSize, y * TileSize - 6)
            sprite.zIndex = 100 + y * TileSize + 16
            const context = tilemapMetaProvider.getMapDeltaAt(x, y, tilemapMetaProvider.resolveEntityTileLayerId())?.context
            this.bonecallerStones.set(sprite, { shrineId: context?.bonecallerShrineId,
                index: context?.bonecallerStoneIndex ?? 0, receivedAt: Date.now() })
            this.surfaceObjectsByKey.set(key, { spriteSet: [sprite], compositeTileId,
                tilesetId: "bonecaller-stones", compositeRoot: { x, y } })
            return
        }

        //
        // dense microtiles
        //
        if (parentTileMeta.tileTypes.includes("dense")) {
            this.renderDenseMicrotiles(key, parentTileId, parentTileMeta, x, y, zIndex)
            return
        }

        //
        // compositeTile
        //

        const tileset = tilemapMetaProvider.resolveTileSet(parentTileId)

        const compositeSprites = this.handleRenderCompositeTile(
            key,
            tilemapMetaProvider,
            compositeTileId,
            tileset.tilesetId,
            x,
            y,
            this.surface,
            zIndex,
        )
        const object = this.surfaceObjectsByKey.get(key)
        // Saved HP must reach the damage pass even on a fresh client with no hit history.
        // Trees use trunk notches rather than authored breakage overlays.
        if (object && (parentTileMeta.breakageMeta || isTreeEntityTile(parentTileMeta.entityTileId))) {
            object.delta = tilemapMetaProvider.findMapDeltaAt(x, y)
        }
    }

    public handleRenderCompositeTile = (
        key: string,
        tilemapMetaProvider: TileMapMetaProvider,
        compositeTileId: string,
        tilesetId: string,
        x: number = 0,
        y: number = 0,
        surface?: Container,
        extraZIndex?: number,
    ): Sprite[] => {
        let set: Sprite[] = this.surfaceObjectsByKey.get(key)?.spriteSet
        if (set) {
            return set
        }
        set = []

        const compositeTileMeta = tilemapMetaProvider.resolveCompositeTileIdTileMeta(compositeTileId, tilesetId)
        if (!compositeTileMeta) {
            return set
        }

        const parentTileSet = tilemapMetaProvider.getTilesetById(tilesetId)
        const parentTileMeta = compositeTileMeta.tileMeta

        //
        // compositeTile
        //
        const { width } = parentTileSet

        // find the parent tile location
        let parentTileSetX
        let parentTileSetY

        const tileNumber = compositeTileMeta.tileNumber - 1
        parentTileSetX = tileNumber % width
        parentTileSetY = Math.floor(tileNumber / width)

        const isDenseVertical = parentTileMeta.tileTypes.includes("denseVertical")
        if (isDenseVertical) {
            this.renderDenseVerticalTiles(
                key,
                compositeTileMeta,
                parentTileSet,
                parentTileSetX,
                parentTileSetY,
                x,
                y,
                surface,
                extraZIndex,
                set,
            )
            this.surfaceObjectsByKey.set(key, {
                spriteSet: set,
                compositeTileId,
                tilesetId,
                compositeRoot: { x, y },
            })
        } else {
            const fullTileId = `${parentTileSet.tilesetId}-${compositeTileId}`

            let texture = this.tileTextureExtractor.extract(
                tilemapMetaProvider,
                parentTileSet.tilesetId,
                compositeTileId,
            )

            const bounds = TileTextureExtractor.compositeTileSpriteBounds.get(fullTileId)
            const children: NumberedTileMeta[] = compositeTileMeta.children || []
            let intersects = false
            for (let i = 0; !this.depthSortedRoofs && i < children.length; i++) {
                const childTileMeta = children[i]
                const tileNumber = childTileMeta.tileNumber - 1
                const tileSetX = tileNumber % width
                const tileSetY = Math.floor(tileNumber / width)

                const childTileSetXOffset = tileSetX - parentTileSetX
                const childTileSetYOffset = tileSetY - parentTileSetY

                const xx = x + childTileSetXOffset
                const yy = y + childTileSetYOffset

                if (tilemapMetaProvider.isSkyTileAt(xx, yy)) {
                    intersects = true
                    break
                }
            }

            if (!intersects) {
                const material = parentTileMeta.entityTileId === "testore"
                    ? tilemapMetaProvider.getMapDeltaAt?.(x, y, tilemapMetaProvider.resolveEntityTileLayerId())?.context?.oreMaterial
                    : undefined
                if (isOreMaterial(material)) texture = oreMineralTexture(texture, material, getRendererApp().renderer)
                if (isTreeEntityTile(parentTileMeta.entityTileId)) {
                    const edges = treeRootEdges(tilesetId, compositeTileId, width, children)
                    if (edges?.length) {
                        const placement = compositeSpritePlacement(compositeTileMeta, bounds, x, y, extraZIndex)
                        const ground = rootGroundPoint(edges)
                        if (grassAt(tilemapMetaProvider, placement.x + ground.x, placement.y + ground.y)) {
                            texture = treeRootFringeTexture(texture, edges, getRendererApp().renderer)
                        }
                    }
                }
                this.renderNonIntersectingCompositeTile(
                    compositeTileMeta,
                    texture,
                    bounds,
                    x,
                    y,
                    surface,
                    extraZIndex,
                    set,
                )

            } else {
                this.renderSkyIntersectingCompositeTile(
                    compositeTileMeta,
                    parentTileSet,
                    x,
                    y,
                    surface,
                    extraZIndex,
                    set,
                    bounds,
                    children,
                    tilemapMetaProvider,
                )
            }

            this.surfaceObjectsByKey.set(key, {
                spriteSet: set,
                compositeTileId,
                tilesetId,
            })
            // Short tufts use the ordinary composite path. Offer their complete sprite to wind
            // as well; GrassSway ignores every texture outside its explicit vegetation list.
            for (const sprite of set) this.offerToWind(sprite, x, y, `${tilesetId}-${tileNumber + 1}`)
        }

        // Tag the tiles the occluder-fade may fade, with the gate policy each kind wants (a structured
        // check on the tile's entityTileId -- no sprite/texture name parsing). Pool sprites are untagged
        // at checkout (SpriteManagementService.attachTexture), so this only needs to stamp the few that
        // fade. A tile's BREAKAGE overlay comes back through here under its own composite id
        // (stoneBreaklevel*, no fade policy) and is then re-parented onto the block it cracks, which is
        // what we want: a pixi child inherits its parent's alpha, and a child's x/y are parent-relative,
        // so the occlusion box math must only ever see the top-level sprite.
        const policy = occluderFadePolicy(parentTileMeta?.entityTileId)
        if (policy) {
            for (const g of set) {
                ;(g as any).occluderFade = policy
            }
        }

        return set
    }

    public renderDenseMicrotiles = (
        key: string,
        parentTileId: string,
        parentTileMeta: any,
        x: number,
        y: number,
        zIndex: number,
    ) => {
        const set = []
        this.surfaceObjectsByKey.set(key, {
            spriteSet: set,
        })

        const fullTileId = this.tilemapMetaProvider.resolveTilesetTileId(parentTileId)
        const cacheKey = `${contentTextureGeneration()}:${fullTileId}`
        let textTure = this.compositeTileSpriteTextures.get(cacheKey)
        if (!textTure) {
            if (parentTileMeta.spriteId) {
                let resource = contentSheetResource("humanoid")
                if (resource) {
                    textTure = resource.texture
                }
            } else {
                textTure = contentTexture(`${fullTileId}`)
                this.compositeTileSpriteTextures.set(cacheKey, textTure)
            }
        }

        const parentFine = roughToFineCoordinates({ x, y })
        const noiseChance = 0.1

        const print = (offsetX: number = 0, offsetY: number = 0) => {
            const g = this.attachTexture(textTure)
            g.x = parentFine.x + offsetX
            g.y = parentFine.y + offsetY
            g.zIndex = zIndex
            set.push(g)
        }

        print()
        this.pctChance(noiseChance, `pct-${key}-${parentFine.x}-${parentFine.y}-${zIndex}`) &&
            print(-0.5 * TileSize, -0.5 * TileSize)
        this.pctChance(noiseChance, `pct-${key}-${parentFine.x}-${parentFine.y}-${zIndex}`) &&
            print(0.5 * TileSize, -0.5 * TileSize)
        this.pctChance(noiseChance, `pct-${key}-${parentFine.x}-${parentFine.y}-${zIndex}`) &&
            print(-0.5 * TileSize, 0.5 * TileSize)
        this.pctChance(noiseChance, `pct-${key}-${parentFine.x}-${parentFine.y}-${zIndex}`) &&
            print(0.5 * TileSize, 0.5 * TileSize)
    }

    public renderDenseVerticalTiles = (
        key: string,
        compositeTileMeta: any,
        parentTileSet: any,
        parentTileSetX: number,
        parentTileSetY: number,
        x: number,
        y: number,
        surface: Container | undefined,
        extraZIndex: number | undefined,
        set: Sprite[],
    ) => {
        const count = this.pctChance(10, `dense-vertical-${key}-1`)
            ? this.pctChance(50, `dense-vertical-${key}-2`)
                ? 1
                : 3
            : 2
        const gradient = TileSize / count

        for (let i = 0; i < count; i++) {
            let verticalOffset = i * gradient
            const yPertubation = this.pertubation("_", x, y, i, `horizontalDensity`)

            const children: NumberedTileMeta[] = compositeTileMeta.children || []
            const parentTileIdNumber = compositeTileMeta.tileNumber
            // The id the composite's parent tile renders under. NOTE the convention change: a tile's
            // position as an AUTHOR writes it is one below the id the client resolves it to (see
            // flora.ts), but compositeTileMeta.tileNumber is already in the resolved convention. Both
            // numbers are small, plausible and name a real neighbouring tile, so getting this wrong
            // matches nothing and reports nothing -- which is exactly what it did first.
            const parentCompositeTextureId = `${parentTileSet.tilesetId}-${parentTileIdNumber}`

            let height = 0
            children.forEach(childTileMeta => {
                const tileId = `${childTileMeta.tileNumber}`
                const tileNumber = childTileMeta.tileNumber - 1
                const tileSetX = tileNumber % parentTileSet.width
                const tileSetY = Math.floor(tileNumber / parentTileSet.width)

                const fullTileId = `${parentTileSet.tilesetId}-${tileId}`

                const isParent = parentTileIdNumber === tileNumber
                const childTileSetXOffset = tileSetX - parentTileSetX
                const childTileSetYOffset = tileSetY - parentTileSetY
                if (!isParent) {
                    height = Math.max(height, Math.abs(childTileSetYOffset))
                }

                const cacheKey = `${contentTextureGeneration()}:${fullTileId}`
                let textTure = this.compositeTileSpriteTextures.get(cacheKey)
                if (!textTure) {
                    textTure = contentTexture(`${fullTileId}`)
                    this.compositeTileSpriteTextures.set(cacheKey, textTure)
                }
                const g = this.attachTexture(textTure, surface)

                const fineItem = CoordinatePool.instance.checkout()
                const roughItem = CoordinatePool.instance.checkout()
                const rough = roughItem.data
                rough.x = x + childTileSetXOffset
                rough.y = y + childTileSetYOffset
                const fine = fineItem.data
                setRoughToFineCoordinates(rough, fine)

                g.x = fine.x - this.pertubation(tileId, fine.x, fine.y, i, `verticalDensity`, 1)
                g.y = fine.y - yPertubation
                g.zIndex =
                    100 +
                        roughToFineCoordinates({ x, y }).y -
                        verticalOffset -
                        yPertubation +
                        TileSize / 3 +
                        extraZIndex || 0

                CoordinatePool.instance.release(roughItem)
                CoordinatePool.instance.release(fineItem)

                g.interactive = false

                // Offer this sub-tile to the wind. Long grass is a two-tile vertical composite -- a
                // base and a top -- so the sub-tile's own height above the anchor row is exactly how
                // much of the bend it should carry: the base barely moves and the top does the
                // leaning, which is how a plant bends rather than how a decal slides.
                //
                // Keyed on the COMPOSITE's parent tile, not on the sub-tile, so one entry in the sway
                // table covers every piece of the plant.
                this.offerToWind(g, x, y, parentCompositeTextureId, -childTileSetYOffset)

                set.push(g)
            })
            if (height > 0 || compositeTileMeta.tileMeta.renderZIdxOffset !== undefined) {
                set.forEach(g => {
                    if (height > 1) {
                        g.y += TileSize
                        g.zIndex += TileSize
                    }
                    if (compositeTileMeta.tileMeta.renderZIdxOffset) {
                        g.zIndex += compositeTileMeta.tileMeta.renderZIdxOffset
                    }
                })
            }
        }
    }

    public renderNonIntersectingCompositeTile = (
        compositeTileMeta: any,
        texture: Texture,
        bounds: Bounds,
        x: number,
        y: number,
        surface: Container | undefined,
        extraZIndex: number | undefined,
        set: Sprite[],
    ) => {
        const surfaceToUse = surface || this.surface
        const containingSprite: Sprite = this.attachTexture(texture, surfaceToUse)
        const placement = compositeSpritePlacement(compositeTileMeta, bounds, x, y, extraZIndex)

        containingSprite.x = placement.x
        containingSprite.y = placement.y
        containingSprite.zIndex = placement.zIndex

        set.push(containingSprite)
    }

    public renderSkyIntersectingCompositeTile = (
        compositeTileMeta: any,
        parentTileSet: any,
        x: number,
        y: number,
        surface: Container | undefined,
        extraZIndex: number | undefined,
        set: Sprite[],
        bounds: Bounds,
        children: NumberedTileMeta[],
        tilemapMetaProvider: TileMapMetaProvider,
    ) => {
        // find the parent tile location
        const tileNumber = compositeTileMeta.tileNumber - 1
        const parentTileSetXLocal = tileNumber % parentTileSet.width
        const parentTileSetYLocal = Math.floor(tileNumber / parentTileSet.width)

        const fineXX = roughToFineCoordinates({ x, y }).x
        // Splitting the artwork around sky tiles must retain the whole composite's ground depth.
        const placement = compositeSpritePlacement(compositeTileMeta, bounds, x, y, extraZIndex)
        const left = Math.min(...children.map(child => (child.tileNumber - 1) % parentTileSet.width))
        const top = Math.min(...children.map(child => Math.floor((child.tileNumber - 1) / parentTileSet.width)))

        children.forEach(childTileMeta => {
            const tileId = `${childTileMeta.tileNumber}`
            const tileNumber = childTileMeta.tileNumber - 1
            const tileSetX = tileNumber % parentTileSet.width
            const tileSetY = Math.floor(tileNumber / parentTileSet.width)

            const fullTileId = `${parentTileSet.tilesetId}-${tileId}`
            const childTileSetXOffset = tileSetX - parentTileSetXLocal
            const childTileSetYOffset = tileSetY - parentTileSetYLocal

            const xx = x + childTileSetXOffset
            const yy = y + childTileSetYOffset

            const skyAt = tilemapMetaProvider.isSkyTileAt(xx, yy)
            const surfaceToUse = skyAt ? this.skySpriteSurfaceAbove : surface
            const subtileSprite = this.attachTexture(contentTexture(fullTileId), surfaceToUse)
            const fineX = placement.x + (tileSetX - left) * TileSize
            const fineY = placement.y + (tileSetY - top) * TileSize

            subtileSprite.x = fineX
            subtileSprite.y = fineY

            subtileSprite.zIndex = placement.zIndex

            set.push(subtileSprite)

            if (false) {
                if (!skyAt) {
                    {
                        const g = new Graphics()
                        g.beginFill(0xfffffff)
                        g.drawCircle(16, 16, 5)
                        g.zIndex = 10000000
                        g.endFill()
                        g.x = fineXX
                        g.y = subtileSprite.zIndex - 100
                        surfaceToUse.addChild(g)
                    }

                    const g = new Graphics()
                    g.beginFill(0xff0000)
                    g.drawCircle(16, 16, 5)
                    g.zIndex = 10000000
                    g.endFill()
                    g.x = fineX
                    g.y = fineY
                    surfaceToUse.addChild(g)
                } else {
                    const g = new Graphics()
                    g.beginFill(0x00ff00)
                    g.drawCircle(16, 16, 5)
                    g.zIndex = 10000000
                    g.endFill()
                    g.x = fineX
                    g.y = fineY
                    surfaceToUse.addChild(g)
                }
            }
        })
    }

    updateSkySurfaces = (skySpriteSurfaceAbove: Container) => {
        this.skySpriteSurfaceAbove = skySpriteSurfaceAbove
    }
}
